import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, LandPlot, Plus, RefreshCw, Search, Upload } from 'lucide-react';

import { useActiveSubject } from '../../context/ActiveSubjectContext';
import { useAuth } from '../../context/AuthContext';
import { assetService } from '../../services/assetService';
import {
  collapseHomeAssetDay,
  ensureHomeAssetDayLoaded,
  expandHomeAssetDay,
  getRecentAvailableDayKeys,
  readHomeAssetDayStore,
  syncHomeAssetDayStore,
  type HomeAssetDayStoreSnapshot,
} from '../../services/homeAssetDayStore';
import { liveUpdatesService, type AssetParticleExtractionLiveEventStatus } from '../../services/liveUpdatesService';
import { isAssetExtractionActive } from '../../utils/assetDisplay';
import type { Asset } from '../../types/domain';
import { buildAssetSections, formatChineseDate, formatDayKey } from '../../utils/assetMedia';
import { isHomeDiaryAsset } from '../../utils/subjectConfig';
import { buildPasStairsHref, findPasSearchRegionId } from '../../utils/pasAsset';
import { AssetCard, buildParticlePreviewFromParticles } from './AssetCard';
import { useAssetFeedColumns } from './ProxyImage';
import { CalendarGemBadge } from '../calendar/CalendarGemBadge';
import type { CalendarDayGem } from '../../utils/calendarGemDays';

const PAGE_SIZE = 20;
const HYDRATION_CONCURRENCY = 4;
const RECENT_DAY_WINDOW = 3;
const DAY_LABEL_PAGE_SIZE = 12;
const HOME_DAY_ASSET_PAGE_ROWS = 2;
const FOCUS_ASSET_STORAGE_KEY = 'psycheflow.web.assetFeed.focusAssetId';

/** Survives React StrictMode remount (sessionStorage alone is consumed on first mount). */
let pendingFocusAssetIdMemory: string | null = null;

type AssetFeedProps = {
  variant?: 'home' | 'library' | 'pas';
  showUpload?: boolean;
  showSearch?: boolean;
  className?: string;
  dayGems?: Record<string, CalendarDayGem>;
};

export function rememberAssetFeedFocus(assetId: string) {
  const id = String(assetId || '').trim();
  if (!id) return;
  pendingFocusAssetIdMemory = id;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(FOCUS_ASSET_STORAGE_KEY, id);
  } catch {
    // ignore quota / private mode
  }
}

function peekAssetFeedFocusId(): string | null {
  if (pendingFocusAssetIdMemory) {
    return pendingFocusAssetIdMemory;
  }
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return String(sessionStorage.getItem(FOCUS_ASSET_STORAGE_KEY) || '').trim() || null;
  } catch {
    return null;
  }
}

function clearAssetFeedFocusId() {
  pendingFocusAssetIdMemory = null;
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(FOCUS_ASSET_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function scrollAssetCardIntoView(assetId: string) {
  const el = document.getElementById(`asset-card-${assetId}`);
  if (!el) return false;

  // Prefer the AppLayout main scroller; fall back to nearest overflow container / window.
  let scroller: HTMLElement | null = el.parentElement;
  while (scroller) {
    const style = window.getComputedStyle(scroller);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
      && scroller.scrollHeight > scroller.clientHeight + 8
    ) {
      break;
    }
    scroller = scroller.parentElement;
  }

  if (scroller) {
    const scrollerRect = scroller.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const nextTop = scroller.scrollTop + (elRect.top - scrollerRect.top)
      - (scroller.clientHeight / 2) + (elRect.height / 2);
    scroller.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  } else {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  return true;
}

export function AssetFeed({
  variant = 'home',
  showUpload = true,
  showSearch = variant === 'library',
  className = '',
  dayGems = {},
}: AssetFeedProps) {
  const isPasFeed = variant === 'pas';
  const isHomeFeed = variant === 'home';
  const isLibraryFeed = variant === 'library';
  const dayTypeFilter = isPasFeed ? 'pas' as const : 'exclude_pas' as const;
  const typeFilter = dayTypeFilter;
  const { user } = useAuth();
  const { activeSubject, hydrated: subjectHydrated } = useActiveSubject();
  const columns = useAssetFeedColumns();
  const [assets, setAssets] = useState<Asset[]>([]);
  const loadGenerationRef = useRef(0);
  const [daySnapshot, setDaySnapshot] = useState<HomeAssetDayStoreSnapshot | null>(null);
  const [visibleDayLimit, setVisibleDayLimit] = useState(DAY_LABEL_PAGE_SIZE);
  const [dayAssetPages, setDayAssetPages] = useState<Record<string, number>>({});
  const [collapsedDayKeys, setCollapsedDayKeys] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Keep date grouping interactive on the home feed; each expanded date still
  // renders only a bounded page of cards below.
  const useDayFeed = isHomeFeed || (isPasFeed || isLibraryFeed) && !search.trim();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [particleCounts, setParticleCounts] = useState<Record<string, number>>({});
  const [particlePreviews, setParticlePreviews] = useState<Record<string, string>>({});
  const [pendingParticleAssetIds, setPendingParticleAssetIds] = useState<Set<string>>(() => new Set());
  const [liveStatus, setLiveStatus] = useState<Record<string, AssetParticleExtractionLiveEventStatus>>({});
  const hydratedIdsRef = useRef(new Set<string>());
  const hydratingIdsRef = useRef(new Set<string>());
  const particleHydrationGenerationRef = useRef(0);
  const particleCountsRef = useRef<Record<string, number>>({});
  particleCountsRef.current = particleCounts;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [highlightedAssetId, setHighlightedAssetId] = useState<string | null>(null);
  const pendingFocusIdRef = useRef<string | null>(peekAssetFeedFocusId());
  const focusHandledRef = useRef(false);

  // Re-arm focus when navigating back onto this feed (StrictMode-safe: peek, don't consume yet).
  useEffect(() => {
    const focusId = peekAssetFeedFocusId();
    if (!focusId) return;
    pendingFocusIdRef.current = focusId;
    focusHandledRef.current = false;
  }, [location.key, variant]);

  const hydrateParticleMeta = useCallback(async (items: Asset[]) => {
    const generation = ++particleHydrationGenerationRef.current;
    const pending = items.filter((asset) => {
      if (hydratedIdsRef.current.has(asset.id)) {
        return false;
      }
      if (hydratingIdsRef.current.has(asset.id)) {
        return false;
      }
      hydratingIdsRef.current.add(asset.id);
      return true;
    });
    if (!pending.length) {
      return;
    }

    setPendingParticleAssetIds((current) => {
      const next = new Set(current);
      for (const asset of pending) {
        next.add(asset.id);
      }
      return next;
    });

    if (isHomeFeed) {
      let counts: Record<string, number> = {};
      let previews: Record<string, string> = {};
      try {
        const meta = await assetService.listKnowledgeParticleCounts(pending.map((asset) => asset.id));
        counts = meta.counts;
        previews = meta.previews;
      } catch {
        counts = { ...particleCountsRef.current };
        previews = {};
      }

      for (const asset of pending) {
        hydratingIdsRef.current.delete(asset.id);
      }
      if (generation !== particleHydrationGenerationRef.current) {
        return;
      }
      for (const asset of pending) {
        hydratedIdsRef.current.add(asset.id);
      }
      setPendingParticleAssetIds((current) => {
        const next = new Set(current);
        for (const asset of pending) {
          next.delete(asset.id);
        }
        return next;
      });
      setParticleCounts((current) => {
        const next = { ...current };
        for (const asset of pending) {
          if (Object.prototype.hasOwnProperty.call(counts, asset.id)) {
            next[asset.id] = counts[asset.id] ?? 0;
          }
        }
        return next;
      });
      setParticlePreviews((current) => {
        const next = { ...current };
        for (const asset of pending) {
          next[asset.id] = String(
            previews[asset.id]
            || asset.particle_preview
            || asset.ai_title
            || asset.ai_brief
            || asset.description
            || current[asset.id]
            || '',
          ).trim();
        }
        return next;
      });
      return;
    }

    const snapshots: Array<{ assetId: string; count: number; preview: string }> = [];

    const workers = Array.from({ length: Math.min(HYDRATION_CONCURRENCY, pending.length) }, async (_, workerIndex) => {
      for (let index = workerIndex; index < pending.length; index += HYDRATION_CONCURRENCY) {
        const asset = pending[index];
        try {
          const particles = await assetService.listKnowledgeParticlesByAsset(asset.id);
          snapshots.push({
            assetId: asset.id,
            count: particles.length,
            preview: buildParticlePreviewFromParticles(particles, {
              softenLatexForLanguageCard: true,
            }),
          });
        } catch {
          const previousCount = particleCountsRef.current[asset.id];
          snapshots.push({
            assetId: asset.id,
            count: typeof previousCount === 'number' ? previousCount : 0,
            preview: '',
          });
        }
      }
    });

    await Promise.all(workers);

    for (const asset of pending) {
      hydratingIdsRef.current.delete(asset.id);
    }
    if (generation !== particleHydrationGenerationRef.current) {
      return;
    }

    for (const asset of pending) {
      hydratedIdsRef.current.add(asset.id);
    }

    setPendingParticleAssetIds((current) => {
      const next = new Set(current);
      for (const asset of pending) {
        next.delete(asset.id);
      }
      return next;
    });

    setParticleCounts((current) => {
      const next = { ...current };
      for (const snapshot of snapshots) {
        next[snapshot.assetId] = snapshot.count;
      }
      return next;
    });

    setParticlePreviews((current) => {
      const next = { ...current };
      for (const snapshot of snapshots) {
        next[snapshot.assetId] = snapshot.preview;
      }
      return next;
    });
  }, [isHomeFeed]);

  const applyHomeSnapshot = useCallback((snapshot: HomeAssetDayStoreSnapshot) => {
    setDaySnapshot(snapshot);
    setVisibleDayLimit((current) => Math.max(current, Math.min(DAY_LABEL_PAGE_SIZE, snapshot.days.length || DAY_LABEL_PAGE_SIZE)));
    const flat: Asset[] = [];
    for (const dayKey of snapshot.expandedDayKeys) {
      flat.push(...(snapshot.assetsByDay[dayKey] || []));
    }
    for (const [dayKey, dayAssets] of Object.entries(snapshot.assetsByDay)) {
      if (!snapshot.expandedDayKeys.includes(dayKey)) {
        flat.push(...dayAssets);
      }
    }
    setAssets(flat);
    setHasMore(false);
    setParticlePreviews((current) => {
      const next = { ...current };
      for (const asset of flat) {
        if (next[asset.id]) continue;
        const seeded = String(
          asset.particle_preview
          || asset.ai_title
          || asset.ai_brief
          || asset.description
          || '',
        ).trim();
        if (seeded) {
          next[asset.id] = seeded;
        }
      }
      return next;
    });
    void hydrateParticleMeta(flat);
  }, [hydrateParticleMeta]);

  const loadPage = useCallback(async (nextPage: number, reset = false) => {
    if (!user?.id) {
      return;
    }

    const loadGeneration = ++loadGenerationRef.current;
    const subjectAtStart = activeSubject;

    if (useDayFeed) {
      const snapshot = await syncHomeAssetDayStore({
        userId: user.id,
        subject: subjectAtStart,
        forceRefresh: reset,
        typeFilter: dayTypeFilter,
      });
      if (loadGeneration !== loadGenerationRef.current) {
        return;
      }
      applyHomeSnapshot(snapshot);
      setPage(1);
      return;
    }

    const result = await assetService.listAssets({
      userId: user.id,
      page: nextPage,
      pageSize: PAGE_SIZE,
      search: showSearch ? search : '',
      sortOrder: 'desc',
      subject: subjectAtStart,
      typeFilter,
    });

    if (loadGeneration !== loadGenerationRef.current) {
      return;
    }

    if (reset) {
      hydratedIdsRef.current.clear();
      hydratingIdsRef.current.clear();
      ++particleHydrationGenerationRef.current;
      setPendingParticleAssetIds(new Set());
    }

    const visibleAssets = isPasFeed
      ? result.data
      : result.data.filter((asset) => isHomeDiaryAsset(asset));
    setAssets((current) => (reset ? visibleAssets : [...current, ...visibleAssets]));
    setPage(result.pagination.page);
    setHasMore(result.pagination.page < result.pagination.totalPages);
    void hydrateParticleMeta(visibleAssets);
  }, [activeSubject, applyHomeSnapshot, dayTypeFilter, hydrateParticleMeta, search, showSearch, typeFilter, useDayFeed, user?.id]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      await loadPage(1, true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [loadPage]);

  const handleToggleHomeDay = useCallback(async (dayKey: string) => {
    if (!user?.id || !useDayFeed) return;
    const snapshot = daySnapshot || readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter);
    const recent = new Set(getRecentAvailableDayKeys(snapshot.days, RECENT_DAY_WINDOW));
    const isExpanded = snapshot.expandedDayKeys.includes(dayKey) || (recent.has(dayKey) && !collapsedDayKeys.has(dayKey));
    if (isExpanded) {
      setCollapsedDayKeys((current) => new Set(current).add(dayKey));
      collapseHomeAssetDay(user.id, activeSubject, dayKey, dayTypeFilter);
      applyHomeSnapshot(readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter));
      return;
    }
    setCollapsedDayKeys((current) => {
      const next = new Set(current);
      next.delete(dayKey);
      return next;
    });
    expandHomeAssetDay(user.id, activeSubject, dayKey, dayTypeFilter);
    applyHomeSnapshot(readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter));
    try {
      await ensureHomeAssetDayLoaded({
        userId: user.id,
        subject: activeSubject,
        dayKey,
        forceRefresh: true,
        typeFilter: dayTypeFilter,
      });
      applyHomeSnapshot(readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    }
  }, [activeSubject, applyHomeSnapshot, collapsedDayKeys, daySnapshot, dayTypeFilter, useDayFeed, user?.id]);

  const skipSearchEffectRef = useRef(true);

  useEffect(() => {
    setVisibleDayLimit(DAY_LABEL_PAGE_SIZE);
  }, [activeSubject, useDayFeed]);

  useEffect(() => {
    if (!user?.id || !subjectHydrated) {
      return;
    }

    setLoading(true);
    void refresh();
  }, [refresh, subjectHydrated, user?.id]);

  useEffect(() => {
    if (!showSearch) {
      return;
    }
    if (skipSearchEffectRef.current) {
      skipSearchEffectRef.current = false;
      return;
    }

    const timer = window.setTimeout(() => {
      setLoading(true);
      void refresh();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [refresh, search, showSearch]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const unsubscribe = liveUpdatesService.subscribe((event) => {
      if (event.kind === 'asset_particle_extraction_state_changed') {
        setLiveStatus((current) => {
          const next = { ...current, [event.assetId]: event.status };
          if (event.status === 'failed' && (event.savedCount ?? 0) > 0) {
            delete next[event.assetId];
          }
          return next;
        });

        if (event.status === 'completed' || event.status === 'failed') {
          hydratedIdsRef.current.delete(event.assetId);
          setPendingParticleAssetIds((current) => {
            const next = new Set(current);
            next.add(event.assetId);
            return next;
          });
          void assetService.listKnowledgeParticleCounts([event.assetId])
            .then((meta) => {
              const count = meta.counts[event.assetId] ?? 0;
              const preview = String(meta.previews[event.assetId] || '').trim();
              setParticleCounts((current) => ({ ...current, [event.assetId]: count }));
              if (preview) {
                setParticlePreviews((current) => ({ ...current, [event.assetId]: preview }));
              }
              if (count > 0) {
                setLiveStatus((current) => {
                  if (!(event.assetId in current)) {
                    return current;
                  }
                  const next = { ...current };
                  delete next[event.assetId];
                  return next;
                });
              }
            })
            .catch(() => undefined)
            .finally(() => {
              hydratedIdsRef.current.add(event.assetId);
              setPendingParticleAssetIds((current) => {
                const next = new Set(current);
                next.delete(event.assetId);
                return next;
              });
            });
        }
      }
    });

    return unsubscribe;
  }, [activeSubject, user?.id]);

  useEffect(() => {
    liveUpdatesService.syncAssetExtractionWatchers(
      assets.filter((asset) => isAssetExtractionActive(asset)),
    );
  }, [assets]);

  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!useDayFeed) {
      if (!hasMore || loading) {
        return;
      }
    } else {
      const totalDays = daySnapshot?.days.length || 0;
      if (loading || visibleDayLimit >= totalDays) {
        return;
      }
    }

    const node = sentinelRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || loadingMoreRef.current) {
        return;
      }

      if (useDayFeed) {
        setVisibleDayLimit((current) => Math.min(
          current + DAY_LABEL_PAGE_SIZE,
          daySnapshot?.days.length || current,
        ));
        return;
      }

      if (!hasMore) {
        return;
      }

      loadingMoreRef.current = true;
      setLoadingMore(true);
      void loadPage(page + 1, false)
        .catch((loadError) => {
          setError(loadError instanceof Error ? loadError.message : '加载更多失败');
        })
        .finally(() => {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    }, { rootMargin: '240px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [daySnapshot?.days.length, hasMore, loadPage, loading, page, useDayFeed, visibleDayLimit]);

  useEffect(() => {
    if (!useDayFeed || !user?.id || !daySnapshot) {
      return;
    }
    const recent = new Set(getRecentAvailableDayKeys(daySnapshot.days, RECENT_DAY_WINDOW));
    const expanded = new Set(daySnapshot.expandedDayKeys);
    const missing = daySnapshot.days
      .slice(0, Math.max(1, visibleDayLimit))
      .filter((entry) => (
        entry.count > 0
        && (expanded.has(entry.dayKey) || recent.has(entry.dayKey))
        && !(daySnapshot.assetsByDay[entry.dayKey] || []).length
      ))
      .map((entry) => entry.dayKey);
    if (!missing.length) {
      return;
    }
    let cancelled = false;
    void Promise.all(missing.map((dayKey) => (
      ensureHomeAssetDayLoaded({
        userId: user.id,
        subject: activeSubject,
        dayKey,
        typeFilter: dayTypeFilter,
      })
    ))).then(() => {
      if (!cancelled) {
        applyHomeSnapshot(readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter));
      }
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [activeSubject, applyHomeSnapshot, daySnapshot, dayTypeFilter, useDayFeed, user?.id, visibleDayLimit]);

  const sections = useMemo(() => {
    if (!useDayFeed || !daySnapshot) {
      return buildAssetSections(assets, columns).map((section) => ({
        ...section,
        expanded: true,
        loading: false,
        page: 0,
        pageCount: 1,
        pageSize: section.rows.length * columns,
      }));
    }

    const recent = new Set(getRecentAvailableDayKeys(daySnapshot.days, RECENT_DAY_WINDOW));
    const expanded = new Set(daySnapshot.expandedDayKeys);
    const loadingDays = new Set(daySnapshot.loadingDayKeys);
    const visibleDays = daySnapshot.days.slice(0, Math.max(1, visibleDayLimit));

    return visibleDays.map((entry) => {
      const isExpanded = expanded.has(entry.dayKey) || (recent.has(entry.dayKey) && !collapsedDayKeys.has(entry.dayKey));
      const dayAssets = isExpanded ? (daySnapshot.assetsByDay[entry.dayKey] || []) : [];
      const built = buildAssetSections(dayAssets, columns)[0];
      return {
        dayKey: entry.dayKey,
        title: formatChineseDate(entry.dayKey),
        count: Math.max(entry.count, dayAssets.length),
        expanded: isExpanded,
        loading: loadingDays.has(entry.dayKey),
        rows: built?.rows || [],
        page: 0,
        pageCount: 1,
        pageSize: dayAssets.length,
      };
    });
  }, [assets, collapsedDayKeys, columns, dayAssetPages, daySnapshot, useDayFeed, visibleDayLimit]);

  // One-shot: after returning from asset detail, locate date → expand → highlight + scroll.
  useEffect(() => {
    if (focusHandledRef.current || loading) return;
    const focusId = pendingFocusIdRef.current || peekAssetFeedFocusId();
    if (!focusId) return;
    pendingFocusIdRef.current = focusId;

    let cancelled = false;
    let resolvedFocusDayKey = '';

    void (async () => {
      if (useDayFeed && user?.id) {
        let focused = assets.find((asset) => asset.id === focusId) || null;
        if (!focused) {
          try {
            focused = await assetService.getAsset(focusId);
          } catch {
            focused = null;
          }
        }
        if (cancelled) return;
        if (!focused) {
          focusHandledRef.current = true;
          pendingFocusIdRef.current = null;
          clearAssetFeedFocusId();
          return;
        }

        const focusDayKey = formatDayKey(focused.created_at);
        resolvedFocusDayKey = focusDayKey;
        const index = (daySnapshot?.days || []).findIndex((entry) => entry.dayKey === focusDayKey);
        if (index >= 0) {
          setVisibleDayLimit((current) => Math.max(current, index + 3, DAY_LABEL_PAGE_SIZE));
        }
        expandHomeAssetDay(user.id, activeSubject, focusDayKey, dayTypeFilter);
        try {
          await ensureHomeAssetDayLoaded({
            userId: user.id,
            subject: activeSubject,
            dayKey: focusDayKey,
            typeFilter: dayTypeFilter,
          });
        } catch {
          // Keep going with cache if any.
        }
        if (cancelled) return;
        applyHomeSnapshot(readHomeAssetDayStore(user.id, activeSubject, dayTypeFilter));
      } else {
        const exists = assets.some((asset) => asset.id === focusId);
        if (!exists) {
          if (!hasMore && !loadingMore) {
            pendingFocusIdRef.current = null;
            focusHandledRef.current = true;
            clearAssetFeedFocusId();
          }
          return;
        }
      }

      if (cancelled) return;
      setHighlightedAssetId(focusId);

      let attempts = 0;
      const maxAttempts = 10;
      const tryScroll = () => {
        if (cancelled) return;
        attempts += 1;
        if (resolvedFocusDayKey && attempts <= 2) {
          document.getElementById(`home-day-${resolvedFocusDayKey}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        const ok = scrollAssetCardIntoView(focusId);
        if (ok || attempts >= maxAttempts) {
          focusHandledRef.current = true;
          pendingFocusIdRef.current = null;
          clearAssetFeedFocusId();
          return;
        }
        window.setTimeout(tryScroll, 80);
      };
      window.setTimeout(tryScroll, 60);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeSubject,
    applyHomeSnapshot,
    assets,
    daySnapshot,
    dayTypeFilter,
    hasMore,
    isHomeFeed,
    loading,
    loadingMore,
    location.key,
    sections,
    useDayFeed,
    user?.id,
  ]);

  return (
    <div className={`space-y-4 ${variant === 'home' ? '' : 'pt-4'} ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div>
          <h2 className={`font-semibold text-ink ${variant === 'home' ? 'text-base sm:text-lg' : 'text-xl sm:text-2xl'}`}>
            {variant === 'home' ? '近期素材' : isPasFeed ? '实战答题纸' : '素材库'}
          </h2>
          {variant === 'home' ? (
            <p className="mt-1 text-sm text-muted">按日期回顾最近上传，点按进入详情。</p>
          ) : isPasFeed ? (
            <p className="mt-1 text-sm text-muted">复习实战答题纸专库：圈选标注与刻意练习提取。</p>
          ) : (
            <p className="mt-1 text-sm text-muted">搜索、上传并管理全部学习素材。</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-2 text-sm text-muted hover:text-primary"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            刷新
          </button>
          {showUpload ? (
            <Link
              to={isPasFeed ? '/scan' : '/assets/upload'}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              {isPasFeed ? <LandPlot size={16} /> : <Upload size={16} />}
              {isPasFeed ? '扫描上传' : '上传'}
            </Link>
          ) : null}
        </div>
      </div>

      {showSearch ? (
        <div className="relative max-w-md px-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={isPasFeed ? '搜索起手方式、路径或练习题干…' : '搜索素材…'}
            className="w-full rounded-lg border border-border py-2 pl-10 pr-3"
          />
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : error ? (
        <div className="mx-1 rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
          {error}
          <button type="button" onClick={() => void refresh()} className="ml-3 underline">重试</button>
        </div>
      ) : (useDayFeed ? !(daySnapshot?.days.length) : assets.length === 0) ? (
        <div className="mx-1 rounded-xl border border-dashed border-border p-8 text-center">
          <p className="text-muted">
            {search.trim()
              ? (isPasFeed ? '没有找到匹配的起手方式、路径或练习题干。' : '没有找到匹配的素材。')
              : isPasFeed
                ? '还没有实战答题纸。可从资源菜单扫描上传。'
                : '还没有素材，上传第一张开始学习。'}
          </p>
          <Link
            to={isPasFeed ? '/scan' : '/assets/upload'}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white"
          >
            {isPasFeed ? <LandPlot size={16} /> : <Plus size={16} />}
            {isPasFeed ? '扫描实战答题纸' : '上传素材'}
          </Link>
        </div>
      ) : (
        <div>
          {sections.map((section) => {
            const dayGem = dayGems[section.dayKey];
            const showRows = !useDayFeed || section.expanded;

            return (
              <section
                key={section.dayKey}
                id={`home-day-${section.dayKey}`}
                className="pb-6"
                style={{ contentVisibility: 'auto', containIntrinsicSize: '0 720px' }}
              >
                <div className="relative sticky top-0 z-20 -mx-2 bg-canvas px-2 py-2 sm:-mx-3 sm:px-3">
                  <div className="pointer-events-none absolute inset-x-0 -top-3 h-3 bg-canvas" aria-hidden />
                  <div className="relative flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <button
                      type="button"
                      disabled={!useDayFeed}
                      onClick={() => { void handleToggleHomeDay(section.dayKey); }}
                      className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1"
                    >
                      {dayGem ? (
                        <CalendarGemBadge seed={dayGem.seed} tier={dayGem.tier} size={16} />
                      ) : null}
                      <h2 className="text-sm font-semibold text-ink">{section.title}</h2>
                      <span className="text-xs text-subtle">{section.count} 项</span>
                      {section.loading ? <Loader2 size={14} className="animate-spin text-primary" /> : null}
                      {useDayFeed ? (
                        <span className="text-xs text-subtle">{section.expanded ? '▾' : '▸'}</span>
                      ) : null}
                    </button>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                </div>
                {showRows ? (
                  <div className="space-y-0.5 pt-2">
                    {section.rows.map((row) => (
                      <div key={row.key} className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                        {Array.from({ length: columns }, (_, columnIndex) => {
                          const asset = row.items[columnIndex];
                          if (!asset) {
                            return <div key={`${row.key}-empty-${columnIndex}`} className="min-h-0" />;
                          }

                          return (
                            <AssetCard
                              key={asset.id}
                              asset={asset}
                              particleCount={particleCounts[asset.id] ?? 0}
                              particlePreview={particlePreviews[asset.id]}
                              particleCountKnown={
                                Object.prototype.hasOwnProperty.call(particleCounts, asset.id)
                                && !pendingParticleAssetIds.has(asset.id)
                              }
                              extractionStatus={liveStatus[asset.id]}
                              highlighted={highlightedAssetId === asset.id}
                              to={
                                isPasFeed && search.trim()
                                  ? buildPasStairsHref(asset.id, findPasSearchRegionId(asset, search))
                                  : `/assets/${asset.id}`
                              }
                              onNavigate={() => rememberAssetFeedFocus(asset.id)}
                            />
                          );
                        })}
                      </div>
                    ))}
                    {useDayFeed && section.expanded && section.pageCount > 1 ? (
                      <div className="grid grid-cols-2 gap-2 pt-2">
                        <button
                          type="button"
                          disabled={section.page <= 0}
                          onClick={() => setDayAssetPages((current) => ({
                            ...current,
                            [section.dayKey]: Math.max(0, section.page - 1),
                          }))}
                          className="min-h-16 rounded-xl border border-dashed border-border bg-canvas px-3 py-2 text-xs font-semibold text-muted transition hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          {section.page > 0 ? '上一页' : '第一页'}
                        </button>
                        <button
                          type="button"
                          disabled={section.page >= section.pageCount - 1}
                          onClick={() => setDayAssetPages((current) => ({
                            ...current,
                            [section.dayKey]: Math.min(section.pageCount - 1, section.page + 1),
                          }))}
                          className="min-h-16 rounded-xl border border-dashed border-primary/35 bg-[#EAF2FF] px-3 py-2 text-xs font-semibold text-primary-strong transition hover:border-primary disabled:cursor-not-allowed disabled:opacity-35"
                        >
                          {section.page >= section.pageCount - 1 ? '已是最后一页' : '查看更多'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}

      <div ref={sentinelRef} className="h-8" />
      {loadingMore || (useDayFeed && visibleDayLimit < (daySnapshot?.days.length || 0)) ? (
        <div className="flex justify-center py-4">
          <Loader2 className="animate-spin text-primary" size={20} />
        </div>
      ) : null}
    </div>
  );
}
